/**
 * Quadro das licitações (D109): uma coluna por status, cartões com número, órgão, UF, modalidade e sessão, no estilo do
 * quadro que a empresa usava no Notion. Arrastar um cartão para outra coluna muda o status pelas mesmas regras do
 * menu (privado.transicao_licitacao_valida); descartar e suspender pedem o motivo. No celular, o status muda pelo
 * chip do cartão.
 */
import { useMemo, useState, type DragEvent } from "react";
import { useGetList, useNotify, useRefresh } from "react-admin";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import EventIcon from "@mui/icons-material/EventOutlined";
import OrgaoIcon from "@mui/icons-material/AccountBalanceOutlined";
import { formatarDataHora, formatarMoeda, normalizarBusca } from "../lib/formato";
import { tokens } from "../tema";
import {
  DialogoMotivo,
  STATUS_LICITACAO,
  SeletorStatus,
  corStatus,
  motivoBloqueio,
  mudarStatus,
  nomeStatus,
  pedeMotivo,
  transicaoPermitida,
  type Acao,
} from "./licitacao-status";
import { NOME_RESULTADO } from "./licitacao-resultado";

interface LicitacaoCartao {
  id: string;
  numero: string;
  orgao_nome: string;
  modalidade_nome: string | null;
  srp: boolean | null;
  uf_destino: string | null;
  data_hora_sessao: string | null;
  status: string;
  objeto: string;
  resultado_geral: string | null;
  valor_estimado_total: number | null;
  busca: string | null;
}

/** Aviso do cartão: o mesmo da lista. */
function aviso(l: LicitacaoCartao): string | null {
  if (l.status === "proposta_pronta" && l.data_hora_sessao && new Date(l.data_hora_sessao).getTime() <= Date.now()) return "Sessão iniciada: a proposta foi enviada?";
  if (["resultado", "recurso", "homologada"].includes(l.status) && (l.resultado_geral === null || l.resultado_geral === "pendente"))
    return l.resultado_geral === null ? "Registrar o resultado" : "Resultado incompleto";
  return null;
}

function Cartao({ l, arrastavel }: { l: LicitacaoCartao; arrastavel: boolean }) {
  const navigate = useNavigate();
  const alerta = aviso(l);
  const passou = l.data_hora_sessao && new Date(l.data_hora_sessao).getTime() < Date.now();
  return (
    <Box
      draggable={arrastavel}
      onDragStart={(e: DragEvent) => {
        e.dataTransfer.setData("text/plain", JSON.stringify({ id: l.id, status: l.status }));
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={() => navigate(`/licitacao/${l.id}`)}
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        p: 1.25,
        cursor: arrastavel ? "grab" : "pointer",
        "&:hover": { borderColor: tokens.ciano, boxShadow: "0 1px 3px rgba(1,38,57,.12)" },
        "&:active": { cursor: arrastavel ? "grabbing" : "pointer" },
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.25 }}>
        {l.numero}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, minWidth: 0 }}>
        <OrgaoIcon sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
        <Typography variant="caption" fontWeight={600} noWrap title={l.orgao_nome}>
          {l.orgao_nome}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mt: 0.25 }} title={l.objeto}>
        {l.objeto}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {l.uf_destino && <Chip size="small" label={l.uf_destino} sx={{ height: 20, fontSize: 11, bgcolor: tokens.gelo, color: tokens.tinta }} />}
        {l.modalidade_nome && (
          <Chip
            size="small"
            label={`${l.modalidade_nome}${l.srp ? " · SRP" : ""}`}
            sx={{ height: 20, fontSize: 11, bgcolor: tokens.navy, color: "#fff", maxWidth: "100%" }}
          />
        )}
      </Stack>
      {l.data_hora_sessao && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
          <EventIcon sx={{ fontSize: 14, color: passou ? "text.disabled" : tokens.ciano }} />
          <Typography variant="caption" color={passou ? "text.disabled" : "text.primary"}>
            {formatarDataHora(l.data_hora_sessao)}
          </Typography>
        </Stack>
      )}
      {l.valor_estimado_total !== null && Number(l.valor_estimado_total) > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          Estimado {formatarMoeda(l.valor_estimado_total)}
        </Typography>
      )}
      {alerta && (
        <Typography variant="caption" color="warning.main" fontWeight={600} display="block" sx={{ mt: 0.5 }}>
          ⚠ {alerta}
        </Typography>
      )}
      {!alerta && l.resultado_geral && l.resultado_geral !== "pendente" && (
        <Typography variant="caption" color={l.resultado_geral.startsWith("ganha") ? "success.main" : "text.secondary"} display="block" sx={{ mt: 0.5 }}>
          {NOME_RESULTADO[l.resultado_geral] ?? ""}
        </Typography>
      )}
      {/* No celular (sem arrastar), o status muda pelo chip. */}
      {!arrastavel && (
        <Box sx={{ mt: 0.75 }} onClick={(e) => e.stopPropagation()}>
          <SeletorStatus licitacaoId={l.id} status={l.status} />
        </Box>
      )}
    </Box>
  );
}

function Coluna({
  status,
  itens,
  arrastavel,
  aoSoltar,
}: {
  status: string;
  itens: LicitacaoCartao[];
  arrastavel: boolean;
  aoSoltar: (dado: { id: string; status: string }, para: string) => void;
}) {
  const [sobre, setSobre] = useState(false);
  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setSobre(true);
      }}
      onDragLeave={() => setSobre(false)}
      onDrop={(e) => {
        e.preventDefault();
        setSobre(false);
        try {
          aoSoltar(JSON.parse(e.dataTransfer.getData("text/plain")) as { id: string; status: string }, status);
        } catch {
          // Arrastou algo que não é cartão: ignora.
        }
      }}
      sx={{
        width: 272,
        flexShrink: 0,
        bgcolor: sobre ? "rgba(0,176,179,.12)" : "#e6ebed",
        border: 1,
        borderColor: sobre ? tokens.ciano : "transparent",
        borderRadius: 2,
        p: 1,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 260px)",
        minHeight: 160,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5, pb: 1 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: corStatus(status), flexShrink: 0 }} />
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {nomeStatus(status)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {itens.length}
        </Typography>
      </Stack>
      <Stack spacing={1} sx={{ overflowY: "auto", pr: 0.25 }}>
        {itens.map((l) => (
          <Cartao key={l.id} l={l} arrastavel={arrastavel} />
        ))}
        {itens.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
            Nenhuma
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/** Quadro: colunas = status do grupo escolhido no painel (filtro igual ao da lista). */
export function QuadroLicitacoes({ filtro, colunas, celular }: { filtro: Record<string, unknown>; colunas: string[]; celular: boolean }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [texto, setTexto] = useState("");
  const [comMotivo, setComMotivo] = useState<{ id: string; acao: Acao } | null>(null);
  const { data = [], error, isPending } = useGetList<LicitacaoCartao>("v_licitacao", {
    filter: filtro,
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "data_hora_sessao", order: "ASC" },
  });

  const porStatus = useMemo(() => {
    const termo = normalizarBusca(texto);
    const m = new Map<string, LicitacaoCartao[]>();
    for (const l of data) {
      if (termo && !(l.busca ?? "").includes(termo)) continue;
      m.set(l.status, [...(m.get(l.status) ?? []), l]);
    }
    return m;
  }, [data, texto]);

  async function soltar(dado: { id: string; status: string }, para: string) {
    if (dado.status === para) return;
    if (dado.status === "suspensa" && para !== "descartada") {
      notify("Licitação suspensa: use “Retomar” no menu do status (volta à etapa anterior).", { type: "info" });
      return;
    }
    if (!transicaoPermitida(dado.status, para)) {
      notify(`Não dá para ir de ${nomeStatus(dado.status)} para ${nomeStatus(para)}: ${motivoBloqueio(para)}.`, { type: "warning" });
      return;
    }
    if (pedeMotivo(para)) {
      setComMotivo({ id: dado.id, acao: { para, rotulo: nomeStatus(para) } });
      return;
    }
    try {
      await mudarStatus(dado.id, para, null);
      notify(`Status: ${nomeStatus(para)}.`, { type: "success" });
      refresh();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
  }

  // Colunas: as do grupo, na ordem dos status.
  const ordem = STATUS_LICITACAO.map((s) => s.id).filter((s) => colunas.includes(s));
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          label="Pesquisar órgão, número, processo ou objeto"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          sx={{ width: { xs: "100%", sm: 360 }, flexShrink: 0 }}
        />
        {!celular && (
          <Typography variant="caption" color="text.secondary">
            Arraste o cartão para mudar o status.
          </Typography>
        )}
      </Stack>
      {error instanceof Error && <Alert severity="error">{error.message}</Alert>}
      {isPending ? (
        <Typography variant="body2" color="text.secondary">
          Carregando…
        </Typography>
      ) : (
        <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1, alignItems: "flex-start" }}>
          {ordem.map((s) => (
            <Coluna key={s} status={s} itens={porStatus.get(s) ?? []} arrastavel={!celular} aoSoltar={(d, para) => void soltar(d, para)} />
          ))}
        </Box>
      )}
      {comMotivo && <DialogoMotivo licitacaoId={comMotivo.id} acao={comMotivo.acao} aoFechar={() => setComMotivo(null)} />}
    </Box>
  );
}
